import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute, { Loading } from "@/components/ProtectedRoute";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";

import Home from "@/pages/Home";
import Discover from "@/pages/Discover";
import Songs from "@/pages/Songs";
import SongDetail from "@/pages/SongDetail";
import Premium from "@/pages/Premium";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Legal from "@/pages/Legal";
import Community from "@/pages/Community";
import Support from "@/pages/Support";
import Copyright from "@/pages/Copyright";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Record from "@/pages/Record";
import Studio from "@/pages/Studio";
import MyRecordings from "@/pages/MyRecordings";
import SavedSongs from "@/pages/SavedSongs";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import PerformanceDetail from "@/pages/PerformanceDetail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Upload from "@/pages/Upload";
import Admin from "@/pages/Admin";
import CatchAll from "@/pages/CatchAll";
import Duet from "@/pages/Duet";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-mesh flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AuthCallback() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        await api.post("/auth/google/session", { session_id: sid });
        const u = await refresh();
        window.history.replaceState(null, "", "/");
        nav(u && !u.onboarded ? "/onboarding" : "/", { replace: true });
      } catch {
        nav("/login", { replace: true });
      }
    })();
  }, [nav, refresh]);
  return <Loading />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Shell><Home /></Shell>} />
      <Route path="/discover" element={<Shell><Discover /></Shell>} />
      <Route path="/songs" element={<Shell><Songs /></Shell>} />
      <Route path="/song/:id" element={<Shell><SongDetail /></Shell>} />
      <Route path="/premium" element={<Shell><Premium /></Shell>} />
      <Route path="/about" element={<Shell><About /></Shell>} />
      <Route path="/faq" element={<Shell><FAQ /></Shell>} />
      <Route path="/legal/:doc" element={<Shell><Legal /></Shell>} />
      <Route path="/community" element={<Shell><Community /></Shell>} />
      <Route path="/support" element={<Shell><Support /></Shell>} />
      <Route path="/copyright" element={<Shell><Copyright /></Shell>} />
      <Route path="/performance/:id" element={<Shell><PerformanceDetail /></Shell>} />
      <Route path="/u/:username" element={<Shell><Profile /></Shell>} />

      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

      <Route path="/record" element={<ProtectedRoute><Shell><Record /></Shell></ProtectedRoute>} />
      <Route path="/studio/:songId" element={<ProtectedRoute><Studio /></ProtectedRoute>} />
      <Route path="/duet/:parentId" element={<ProtectedRoute><Duet /></ProtectedRoute>} />
      <Route path="/recordings" element={<ProtectedRoute><Shell><MyRecordings /></Shell></ProtectedRoute>} />
      <Route path="/saved" element={<ProtectedRoute><Shell><SavedSongs /></Shell></ProtectedRoute>} />
      <Route path="/upload" element={<ProtectedRoute><Shell><Upload /></Shell></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Shell><Billing /></Shell></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Shell><Settings /></Shell></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Shell><Notifications /></Shell></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><Shell><PaymentSuccess /></Shell></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute admin><Admin /></ProtectedRoute>} />
      <Route path="*" element={<Shell><CatchAll /></Shell>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" richColors theme="dark" />
      </BrowserRouter>
    </AuthProvider>
  );
}
